import { Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { format, parseISO } from "date-fns";
import React from "react";
import { TripWithTenant } from "../../../types";
import { isInfinityDate, safeParseDate } from "../../../utils/dateHelpers";

interface ScheduleListPresenterProps {
  // Data
  filteredTrips: TripWithTenant[];
  trips: TripWithTenant[];
  tripPlacements: Record<string, string>;

  // Filters
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  tailNumberFilter: string;
  setTailNumberFilter: (filter: string) => void;
  showFilters?: boolean; // Whether to show filter UI

  // Display properties
  loading?: boolean;
  startDate?: Date;
  endDate?: Date;

  // Callbacks
  onTripEdit?: (trip: TripWithTenant) => void;

  // Helper functions
  formatDate: (dateString: string) => string;
  getStatusColor: (status: string) => string;
  getTripType: (trip: TripWithTenant) => string;
  getTripAssignments: (trip: TripWithTenant) => string;
}

export const ScheduleListPresenter: React.FC<ScheduleListPresenterProps> = ({
  filteredTrips,
  trips,
  tripPlacements,
  statusFilter,
  setStatusFilter,
  tailNumberFilter,
  setTailNumberFilter,
  showFilters = true,
  loading = false,
  startDate,
  endDate,
  onTripEdit,
  formatDate,
  getStatusColor,
  getTripType,
  getTripAssignments,
}) => {
  // DataGrid column definitions
  const columns: GridColDef[] = [
    {
      field: "tail_number",
      headerName: "Tail Number",
      width: 150,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" fontWeight="medium">
            {params.value || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "aircraft_model",
      headerName: "Aircraft Model",
      width: 200,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2">{params.value || "—"}</Typography>
        </Box>
      ),
    },
    {
      field: "type",
      headerName: "Type",
      width: 120,
      align: "left",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => getTripType(row),
      renderCell: (params) => {
        const tripType = getTripType(params.row);
        return (
          <Chip
            label={tripType}
            size="small"
            color={tripType === "Base Tenant" ? "secondary" : "default"}
            variant="outlined"
          />
        );
      },
    },
    {
      field: "arrival_date",
      headerName: "Arrival",
      width: 180,
      type: "dateTime",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => parseISO(row.arrival_date),
      renderCell: (params) => formatDate(params.row.arrival_date),
    },
    {
      field: "departure_date",
      headerName: "Departure",
      width: 180,
      type: "dateTime",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => {
        // Handle infinity dates for sorting (sort to end)
        if (isInfinityDate(row.departure_date)) {
          return new Date('2099-12-31T23:59:59Z');
        }
        return parseISO(row.departure_date);
      },
      renderCell: (params) => formatDate(params.row.departure_date),
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value) as any}
          variant="outlined"
        />
      ),
    },
    {
      field: "notes",
      headerName: "Notes",
      width: 200,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          {params.value ? (
            <Tooltip title={params.value} arrow>
              <Typography
                variant="body2"
                noWrap
                sx={{
                  maxWidth: 180,
                  cursor: "help",
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "owner_manager",
      headerName: "Owner/Manager",
      width: 180,
      align: "left",
      filterable: true,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2">{params.value || "—"}</Typography>
        </Box>
      ),
    },
    {
      field: "assignment",
      headerName: "Assignment",
      width: 180,
      align: "left",
      filterable: true,
      sortable: true,
      valueGetter: (_, row) => getTripAssignments(row),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={() => onTripEdit?.(params.row)}
          title="Edit Trip"
        >
          <EditIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* Date range indicator */}
      {startDate && endDate && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Showing trips from {format(startDate, "MMM d, yyyy")} to{" "}
          {format(endDate, "MMM d, yyyy")}
        </Typography>
      )}

      {/* Filters - only show if not controlled from parent */}
      {showFilters && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Filter by Tail Number"
            variant="outlined"
            size="small"
            value={tailNumberFilter}
            onChange={(e) => setTailNumberFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            placeholder="e.g. N123AB"
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ alignSelf: "center" }}
          >
            Showing {filteredTrips.length} of {trips.length} trips
          </Typography>
        </Stack>
      )}

      {/* Trip count when filters are hidden */}
      {!showFilters && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1, textAlign: "right" }}
        >
          Showing {filteredTrips.length} of {trips.length} trips
        </Typography>
      )}

      {/* DataGrid Table */}
      <Paper sx={{ height: 600, width: "100%" }}>
        <DataGrid
          rows={filteredTrips}
          columns={columns}
          getRowId={(row) => row.trip_id}
          // Enable sorting & filtering
          sortingOrder={["asc", "desc"]}
          filterMode="client"
          // Pagination
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 50 } },
          }}
          // UI Configuration
          disableRowSelectionOnClick
          // Loading state
          loading={loading}
          // No rows overlay
          slots={{
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                <Typography>No trips found</Typography>
              </Stack>
            ),
          }}
        />
      </Paper>
    </Box>
  );
};